import React from 'react';
import { useSearchParams } from 'react-router-dom';

type NoticeType =
  | 'phone-removed'
  | 'already-removed'
  | 'phone-changed'
  | 'link-invalid'
  | 'link-expired'
  | 'not-supported';

const MESSAGES: Record<NoticeType, { title: string; titleColor: string; body: (value: string) => React.ReactNode }> = {
  'phone-removed': {
    title: 'Phone removed',
    titleColor: '#16a34a',
    body: (value) => (
      <>
        <p style={styles.text}>
          The phone number {value ? <strong>{value}</strong> : 'on your account'} has been removed.
        </p>
        <p style={styles.text}>Anyone signed in with that phone has also been signed out.</p>
        <p style={styles.muted}>
          If you didn't expect this email at all, please contact support so we can review your account.
        </p>
      </>
    ),
  },
  'already-removed': {
    title: 'Already removed',
    titleColor: '#1e3c72',
    body: () => (
      <p style={styles.text}>The identifier has already been removed from your account.</p>
    ),
  },
  'phone-changed': {
    title: 'No change made',
    titleColor: '#1e3c72',
    body: (value) => (
      <p style={styles.text}>
        The phone number {value ? <strong>{value}</strong> : ''} is no longer attached to this account, so nothing was changed.
      </p>
    ),
  },
  'link-invalid': {
    title: 'Invalid link',
    titleColor: '#1e3c72',
    body: () => (
      <p style={styles.text}>
        This revocation link is not valid. If you got here by clicking a link in an email, the email may be malformed — please contact support.
      </p>
    ),
  },
  'link-expired': {
    title: 'Link expired',
    titleColor: '#1e3c72',
    body: () => (
      <>
        <p style={styles.text}>This revocation link has expired. Revocation links are valid for 7 days.</p>
        <p style={styles.muted}>
          If you still want to remove a phone or email from your account, sign in to Kizu and update your account settings, or contact support.
        </p>
      </>
    ),
  },
  'not-supported': {
    title: 'Not supported',
    titleColor: '#1e3c72',
    body: () => (
      <p style={styles.text}>Email-revocation links are not currently supported. Please contact support.</p>
    ),
  },
};

export const SecurityNotice: React.FC = () => {
  const [searchParams] = useSearchParams();
  const type = (searchParams.get('type') || 'link-invalid') as NoticeType;
  const value = searchParams.get('value') || '';
  const message = MESSAGES[type] || MESSAGES['link-invalid'];

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={{ ...styles.title, color: message.titleColor }}>Kizu</h1>
        <h2 style={styles.subtitle}>{message.title}</h2>
        {message.body(value)}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    margin: 0,
    padding: 0,
    background: '#f5f5f5',
    minHeight: '100vh',
  },
  card: {
    maxWidth: 480,
    margin: '80px auto',
    background: 'white',
    borderRadius: 12,
    padding: 40,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  title: {
    color: '#1e3c72',
    fontSize: 24,
    marginBottom: 8,
  },
  subtitle: {
    color: '#333',
    fontSize: 20,
    marginTop: 16,
    marginBottom: 16,
  },
  text: {
    color: '#555',
    lineHeight: 1.6,
    fontSize: 16,
  },
  muted: {
    color: '#888',
    fontSize: 14,
    lineHeight: 1.5,
    marginTop: 16,
  },
};
